import React, { useState, useEffect } from "react";
import { apiV2Service } from "../../../services/apiV2Service";
import { FaRegEye as FaEye, FaRegEyeSlash as FaEyeSlash } from "react-icons/fa";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";
import { SpaciaPasswordGuidelines } from "../../../components/SpaciaPasswordGuidelines";
import { trackAnalyticsEvent, AnalyticsEvents } from "../../../utils/analytics";

const Password = () => {
  const [password, setPassword] = useState("");
  const [newpassword, setNewpassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [isEmptyPassword, setIsEmptyPassword] = useState(false);
  const [isEmptyNewPassword, setIsEmptyNewPassword] = useState(false);
  const [isInvalidConfirmPassword, setIsInvalidConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [validCredentials, setValidCredentials] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Fetch password policy for validation ONLY - display is handled by component
  useEffect(() => {
    const fetchPasswordPolicy = async () => {
      try {
        const response = await apiV2Service.getDefaultPasswordPolicy();
        if (response.status === 200) {
          setPasswordPolicy(response.data.data);
        }
      } catch (error) {
        logger.error("Failed to fetch password policy:", error);
      }
    };
    fetchPasswordPolicy();
  }, []);

  const errorUpdatePassword = (error, message) => {
    setErrorMessage(message || "Failed to update password");
    if (error === 401 || error === 403) {
      setInvalidCredentials(true);
      setErrorMessage("Current password is incorrect");
    } else {
      setInvalidCredentials(true);
    }
  };

  const successfullyUpdatePassword = res => {
    setValidCredentials(true);
    setPassword("");
    setNewpassword("");
    setConfirmPassword("");
    setErrorMessage("");
    trackAnalyticsEvent(AnalyticsEvents.PASSWORD_CHANGED, { context: "settings" });
    showApiMessage(res, "update password");
  };

  const updatePassword = async () => {
    // Reset all error states
    setIsEmptyPassword(false);
    setIsEmptyNewPassword(false);
    setIsInvalidConfirmPassword(false);
    setInvalidCredentials(false);
    setValidCredentials(false);
    setErrorMessage("");

    // Frontend validation
    let hasErrors = false;

    if (!password.trim()) {
      setIsEmptyPassword(true);
      hasErrors = true;
    }

    if (!newpassword.trim()) {
      setIsEmptyNewPassword(true);
      hasErrors = true;
    }

    if (!confirmPassword.trim()) {
      setIsInvalidConfirmPassword(true);
      hasErrors = true;
    } else if (newpassword !== confirmPassword) {
      setIsInvalidConfirmPassword(true);
      hasErrors = true;
    }

    // Apply password policy validation if available
    if (passwordPolicy && newpassword) {
      const errors = [];

      if (newpassword.length < passwordPolicy.minLength) {
        errors.push(`Password must be at least ${passwordPolicy.minLength} characters.`);
      }
      if (passwordPolicy.maxLength > 0 && newpassword.length > passwordPolicy.maxLength) {
        errors.push(`Password must be no more than ${passwordPolicy.maxLength} characters.`);
      }
      if (passwordPolicy.minUppercaseCount > 0) {
        const uppercaseCount = (newpassword.match(/[A-Z]/g) || []).length;
        if (uppercaseCount < passwordPolicy.minUppercaseCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minUppercaseCount} uppercase letter(s).`);
        }
      }
      if (passwordPolicy.minLowercaseCount > 0) {
        const lowercaseCount = (newpassword.match(/[a-z]/g) || []).length;
        if (lowercaseCount < passwordPolicy.minLowercaseCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minLowercaseCount} lowercase letter(s).`);
        }
      }
      if (passwordPolicy.minDigitCount > 0) {
        const digitCount = (newpassword.match(/\d/g) || []).length;
        if (digitCount < passwordPolicy.minDigitCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minDigitCount} digit(s).`);
        }
      }
      if (passwordPolicy.minSymbolCount > 0) {
        const symbolCount = (newpassword.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || []).length;
        if (symbolCount < passwordPolicy.minSymbolCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minSymbolCount} special character(s).`);
        }
      }

      if (errors.length > 0) {
        setPasswordErrors(errors);
        setIsEmptyNewPassword(true);
        hasErrors = true;
      }
    } else if (newpassword.length > 0 && newpassword.length < 6) {
      // Fallback to basic validation if no policy
      setIsEmptyNewPassword(true);
      hasErrors = true;
    }

    // Stop if validation fails
    if (hasErrors) {
      return;
    }

    setIsUpdating(true);

    try {
      const payload = {
        password: password, // current password
        newPassword: newpassword, // new password
      };

      logger.log("Updating password with payload:", {
        password: "***",
        newPassword: "***",
      });
      const res = await apiV2Service.updateSelfPassword(payload);
      logger.log("Password update response:", res);
      successfullyUpdatePassword(res);
    } catch (error) {
      logger.error("Password update error:", error);
      const errorMessage = error.response?.data?.message || "Failed to update password";
      errorUpdatePassword(error.response?.status, errorMessage);
      showApiMessage(error, "update password");
    } finally {
      setIsUpdating(false);
    }
  };

  const togglePasswordVisibility = e => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };

  const toggleNewPasswordVisibility = e => {
    e.preventDefault();
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = e => {
    e.preventDefault();
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="space-y-4">
      {/* toasts are handled via showApiMessage; inline alerts removed */}

      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Password & Security</h2>
        <p className="text-sm text-gray-600">Update your password to keep your account secure</p>
      </div>

      {/* Password Form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Current Password */}
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Current Password *
            </label>
            <div className="relative">
              <input
                id="currentPassword"
                type={showPassword ? "text" : "password"}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm pr-10 ${
                  isEmptyPassword ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter current password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  if (isEmptyPassword) setIsEmptyPassword(false);
                  if (invalidCredentials) setInvalidCredentials(false);
                }}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
              </button>
              {isEmptyPassword && <p className="mt-1 text-sm text-red-500">Current password is required</p>}
            </div>
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              New Password *
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm pr-10 ${
                  isEmptyNewPassword ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter new password"
                value={newpassword}
                onChange={e => {
                  setNewpassword(e.target.value);
                  if (isEmptyPassword) setIsEmptyPassword(false);
                  if (isEmptyNewPassword) setIsEmptyNewPassword(false);
                  // Clear confirm password error if passwords now match
                  if (isInvalidConfirmPassword && confirmPassword && e.target.value === confirmPassword) {
                    setIsInvalidConfirmPassword(false);
                  }
                  // Clear password errors when user starts typing
                  if (passwordErrors.length > 0) {
                    setPasswordErrors([]);
                  }
                }}
              />
              <button
                type="button"
                onClick={toggleNewPasswordVisibility}
                className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showNewPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
              </button>
              {isEmptyNewPassword && (
                <p className="mt-1 text-sm text-red-500">
                  {passwordErrors.length > 0 ? passwordErrors.join(" ") : "New password is required"}
                </p>
              )}
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password *
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm pr-10 ${
                  isInvalidConfirmPassword
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={e => {
                  setConfirmPassword(e.target.value);
                  // Clear error if passwords now match or if field was previously empty
                  if (isInvalidConfirmPassword && (e.target.value === newpassword || !e.target.value)) {
                    setIsInvalidConfirmPassword(false);
                  }
                }}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
              </button>
              {isInvalidConfirmPassword && <p className="mt-1 text-sm text-red-500">Passwords do not match</p>}
            </div>
          </div>

          {/* Password Policy Requirements */}
          <SpaciaPasswordGuidelines />

          {/* Update Button */}
          <div className="pt-4">
            <button
              onClick={updatePassword}
              disabled={isUpdating}
              className={`inline-flex items-center px-6 py-2 text-white text-sm rounded-md transition-colors ${
                isUpdating ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isUpdating ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                "Update Password"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Password;
